import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import API from "../lib/api";
import PullToRefresh from "../components/PullToRefresh";
import { CardListSkeleton } from "../components/Skeletons";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { fmtDate } from "../lib/dateUtil";
import {
  Plus, MagnifyingGlass, Receipt, Trash, Copy, PencilSimple,
  ArrowRight, Funnel, FilePdf, DownloadSimple
} from "@phosphor-icons/react";

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  sent: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  approved: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  declined: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",
  expired: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  converted: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  submitted: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  accepted: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
};

export default function QuotesPage() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [stats, setStats] = useState({});
  const [selectedQuotes, setSelectedQuotes] = useState([]);
  const [batchExporting, setBatchExporting] = useState(false);

  const fetchQuotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const [quotesRes, statsRes] = await Promise.all([
        API.get("/quotes", { params }),
        API.get("/quotes/stats"),
      ]);
      setQuotes(quotesRes.data.quotes || []);
      setTotal(quotesRes.data.total || 0);
      setStats(statsRes.data.by_status || {});
    } catch { toast.error("Failed to load quotes"); }
    finally { setLoading(false); }
  }, [search, statusFilter]);

  useEffect(() => { fetchQuotes(); }, [fetchQuotes]);

  const handleDuplicate = async (id) => {
    try {
      const { data } = await API.post(`/quotes/${id}/duplicate`);
      toast.success(`Quote duplicated: ${data.reference}`);
      fetchQuotes();
    } catch { toast.error("Failed to duplicate"); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this quote?")) return;
    try {
      await API.delete(`/quotes/${id}`);
      toast.success("Quote deleted");
      fetchQuotes();
    } catch { toast.error("Failed to delete"); }
  };

  const handleDownloadPdf = async (id, view, ref) => {
    try {
      const response = await API.get(`/quotes/${id}/pdf?view=${view}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${ref || "quote"}_${view}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Failed to download PDF"); }
  };

  const handleBatchExport = async (view) => {
    if (!selectedQuotes.length) return;
    setBatchExporting(true);
    try {
      const response = await API.post("/quotes/batch-pdf", { quote_ids: selectedQuotes, view }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `quotes_${view}.zip`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${selectedQuotes.length} PDFs exported`);
      setSelectedQuotes([]);
    } catch { toast.error("Failed to export"); }
    finally { setBatchExporting(false); }
  };

  const toggleSelectQuote = (id) => setSelectedQuotes(prev => prev.includes(id) ? prev.filter(q => q !== id) : [...prev, id]);
  const toggleSelectAll = () => setSelectedQuotes(prev => prev.length === quotes.length ? [] : quotes.map(q => q.id));

  const statFilters = [
    { key: "", label: "All", count: total },
    { key: "draft", label: "Draft", count: stats.draft || 0 },
    { key: "sent", label: "Sent", count: stats.sent || 0 },
    { key: "approved", label: "Approved", count: stats.approved || 0 },
    { key: "declined", label: "Declined", count: stats.declined || 0 },
    { key: "converted", label: "Converted", count: stats.converted || 0 },
  ];

  return (
    <PullToRefresh onRefresh={fetchQuotes}>
    <div data-testid="quotes-page">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
            <Receipt size={18} className="text-white" weight="fill" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Quotes</h1>
            <p className="text-xs text-muted-foreground">{total} quote{total !== 1 ? "s" : ""}</p>
          </div>
        </div>
        <Button onClick={() => navigate("/quotes/new")} data-testid="create-quote-btn">
          <Plus size={16} className="mr-1.5" /> New Quote
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
        <div className="relative flex-1 max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search quotes..."
            className="pl-9 h-9 text-sm"
            data-testid="quote-search"
          />
        </div>
        <div className="flex items-center gap-1 overflow-x-auto" data-testid="quote-status-filters">
          {statFilters.map(f => (
            <button key={f.key} onClick={() => setStatusFilter(f.key)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${statusFilter === f.key ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-muted"}`}
              data-testid={`filter-${f.key || "all"}`}
            >
              {f.label} ({f.count})
            </button>
          ))}
        </div>
      </div>

      {/* Batch Actions */}
      {selectedQuotes.length > 0 && (
        <div className="flex items-center gap-3 mb-4 p-3 bg-primary/5 border border-primary/20 rounded-md" data-testid="batch-actions-bar">
          <span className="text-sm font-medium">{selectedQuotes.length} selected</span>
          <Button size="sm" onClick={() => handleBatchExport("client")} disabled={batchExporting} data-testid="batch-client-pdf">
            <DownloadSimple size={14} className="mr-1" /> Client PDFs
          </Button>
          <Button size="sm" variant="outline" onClick={() => handleBatchExport("admin")} disabled={batchExporting} data-testid="batch-admin-pdf">
            <DownloadSimple size={14} className="mr-1" /> Admin PDFs
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setSelectedQuotes([])} data-testid="batch-clear">Clear</Button>
        </div>
      )}

      {/* List */}
      {loading ? (
        <CardListSkeleton rows={6} showHeader={false} />
      ) : quotes.length === 0 ? (
        <div className="text-center py-16 border border-dashed border-border rounded-lg" data-testid="no-quotes">
          <Receipt size={36} className="mx-auto text-muted-foreground/40 mb-2" />
          <p className="text-sm text-muted-foreground">No quotes found</p>
          <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate("/quotes/new")}>Create your first quote</Button>
        </div>
      ) : (
        <div className="space-y-2">
          {quotes.map(q => (
            <div key={q.id} className="border border-border rounded-lg p-4 bg-card hover:shadow-sm transition-shadow" data-testid={`quote-row-${q.id}`}>
              <div className="flex items-start gap-3">
                <input type="checkbox" checked={selectedQuotes.includes(q.id)} onChange={() => toggleSelectQuote(q.id)} className="mt-1.5 accent-primary shrink-0" data-testid={`select-quote-${q.id}`} />
                <div className="flex-1 min-w-0 cursor-pointer" onClick={() => navigate(`/quotes/${q.id}`)}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[10px] font-mono text-muted-foreground">{q.reference}</span>
                        <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES[q.status] || STATUS_STYLES.draft}`}>
                          {q.status?.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                        </span>
                        {q.source === "vendor_portal" && (
                          <Badge className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 text-[9px]" data-testid={`quote-supplier-chip-${q.id}`}>Supplier Portal</Badge>
                        )}
                        {q.converted_to && (
                          <Badge variant="outline" className="text-[9px]">
                            {q.converted_to.type === "job" ? "Job" : "Project"}: {q.converted_to.reference}
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm font-semibold truncate">{q.title}</p>
                      <div className="flex items-center gap-3 mt-1 text-[10px] text-muted-foreground">
                        {q.client_name && <span>{q.client_name}</span>}
                        {q.street_address && <span>{q.street_address}, {q.town_city}</span>}
                        <span>{(q.line_items || []).length} item{(q.line_items || []).length !== 1 ? "s" : ""}</span>
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-base font-bold font-[Chivo]">&pound;{(q.vat_enabled ? (q.total_inc_vat || q.grand_total || 0) : (q.grand_total || 0)).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</p>
                      {q.vat_enabled && <p className="text-[9px] text-muted-foreground">inc. VAT</p>}
                      <p className="text-[10px] text-muted-foreground">{fmtDate(q.created_at)}</p>
                    </div>
                  </div>
                </div>
              </div>
              {/* Actions row */}
              <div className="flex items-center gap-1 mt-2 pt-2 border-t border-border/50">
                <Button variant="ghost" size="sm" className="h-7 text-[10px] px-2" onClick={() => navigate(`/quotes/${q.id}`)} data-testid={`view-quote-${q.id}`}>
                  <PencilSimple size={12} className="mr-1" /> View
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-[10px] px-2" onClick={() => handleDuplicate(q.id)} data-testid={`dup-quote-${q.id}`}>
                  <Copy size={12} className="mr-1" /> Duplicate
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-[10px] px-2" onClick={() => handleDownloadPdf(q.id, "client", q.reference)} data-testid={`pdf-client-${q.id}`}>
                  <FilePdf size={12} className="mr-1" /> Client PDF
                </Button>
                <Button variant="ghost" size="sm" className="h-7 text-[10px] px-2" onClick={() => handleDownloadPdf(q.id, "admin", q.reference)} data-testid={`pdf-admin-${q.id}`}>
                  <FilePdf size={12} className="mr-1" /> Admin PDF
                </Button>
                <div className="flex-1" />
                <Button variant="ghost" size="sm" className="h-7 text-[10px] px-2 text-destructive hover:text-destructive" onClick={() => handleDelete(q.id)} data-testid={`del-quote-${q.id}`}>
                  <Trash size={12} />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
    </PullToRefresh>
  );
}
